import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, Optional } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { IonContent, IonIcon, IonSpinner } from "@ionic/angular/standalone";
import { addIcons } from 'ionicons';
import {
  analyticsOutline,
  hardwareChipOutline,
  pulseOutline,
  serverOutline,
  thermometerOutline,
  timeOutline,
  wifiOutline
} from 'ionicons/icons';
import { Subject, takeUntil } from 'rxjs';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';
import { NgxEchartsDirective, provideEchartsCore } from 'ngx-echarts';
import * as echarts from 'echarts/core';
import { LineChart } from 'echarts/charts';
import { GridComponent, LegendComponent, TooltipComponent } from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import { EChartsOption } from 'echarts/types/dist/shared';
import { ModalContainerComponent } from "../../../shared/components/modal-container/modal-container.component";
import { IBreadcrumbs } from '../../../shared/components/breadcrumbs/breadcrumbs.component';
import { environment } from '../../../../environments/environment';
import { MonitoringPreferencesService } from '../../../core/services/monitoring-preferences.service';

echarts.use([LineChart, TooltipComponent, LegendComponent, GridComponent, CanvasRenderer]);

interface ITelemetrySocketMessage {
  type: string;
  timestamp?: string;
  message?: string;
  data?: ITelemetryPayload;
}

interface ITelemetryPayload {
  deviceId: string;
  timestamp: string;
  metrics: ITelemetryMetrics;
  source?: string;
  partitionId?: string;
  sequenceNumber?: number;
  enqueuedTime?: string;
}

interface ITelemetryMetrics {
  cpu_usage_pct?: number | null;
  memory_usage_pct?: number | null;
  memory_used_bytes?: number | null;
  memory_total_bytes?: number | null;
  disk_usage_pct?: number | null;
  disk_used_bytes?: number | null;
  disk_total_bytes?: number | null;
  temperature_celsius?: number | null;
  load_average_1m?: number | null;
  uptime_seconds?: number | null;
  cpu_count?: number | null;
  boot_time?: string | null;
  service_uptime?: number | null;
  system_uptime?: number | null;
  wifi?: {
    signal_level_dbm?: number | null;
  } | null;
}

interface ITelemetryPoint {
  label: string;
  cpuUsage: number | null;
  memoryUsage: number | null;
  diskUsage: number | null;
  temperature: number | null;
  wifiSignal: number | null;
  loadAverage: number | null;
}

interface IChartAxisConfig {
  name: string;
  unit?: string;
  min?: number;
  max?: number;
  splitLine?: boolean;
  position?: 'left' | 'right';
}

interface IChartSeriesConfig {
  name: string;
  data: (number | null)[];
  color: string;
  areaColor: string;
  unit?: string;
  decimals?: number;
  yAxisIndex?: number;
}

@Component({
  selector: 'app-live-telemetry',
  standalone: true,
  templateUrl: './live-telemetry.component.html',
  styleUrls: ['./live-telemetry.component.scss'],
  imports: [
    CommonModule,
    IonContent,
    IonSpinner,
    IonIcon,
    ModalContainerComponent,
    NgxEchartsDirective
  ],
  providers: [
    provideEchartsCore({ echarts }),
  ]
})
export class LiveTelemetryPage implements OnInit, OnDestroy {

  private readonly maxDataPoints = 15;
  private readonly reconnectDelayMs = 3000;
  private readonly destroy$ = new Subject<void>();

  private telemetrySocket?: WebSocketSubject<ITelemetrySocketMessage | { type: 'pong'; timestamp: string }>;
  private reconnectTimer?: ReturnType<typeof setTimeout>;
  private isComponentDestroyed: boolean = false;
  private monitoringAccessLost = false;
  private resource: any;
  private accessToken: string;

  public breadcrumbsList: IBreadcrumbs[] = [];
  public deviceLabel: string;
  public telemetrySocketUrl: string;
  public latestTelemetry: ITelemetryPayload | null = null;
  public telemetryHistory: ITelemetryPoint[] = [];
  public connectionState: 'connecting' | 'connected' | 'disconnected' | 'error' = 'connecting';
  public connectionMessage: string = 'Connessione realtime in preparazione...';
  public lastError: string | null = null;

  public cpuChartOptions: EChartsOption = {};
  public usageChartOptions: EChartsOption = {};
  public temperatureChartOptions: EChartsOption = {};
  public connectivityChartOptions: EChartsOption = {};

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    @Optional() private monitoringPreferences: MonitoringPreferencesService | null = null,
  ) {
    const routeData = this.route.snapshot.data['data'] || {};
    const navigationState = this.router.getCurrentNavigation()?.extras.state || {};

    this.resource = {
      ...routeData,
      ...navigationState
    };
    this.deviceLabel = this.resource.additionalData?.label || this.route.snapshot.paramMap.get('deviceLabel') || '';
    this.accessToken = localStorage.getItem('ACCESS_TOKEN') || '';
    this.telemetrySocketUrl = this.buildTelemetrySocketUrl();
    this.breadcrumbsList = this.resource.breadcrumbs || [
      { icon: 'analytics', label: 'LIVE TELEMETRY' },
      ...(this.deviceLabel ? [{ label: this.deviceLabel }] : [])
    ];

    addIcons({
      'analytics-outline': analyticsOutline,
      'pulse-outline': pulseOutline,
      'hardware-chip-outline': hardwareChipOutline,
      'server-outline': serverOutline,
      'thermometer-outline': thermometerOutline,
      'wifi-outline': wifiOutline,
      'time-outline': timeOutline,
    });

    this.refreshChartOptions();
  }

  ngOnInit() {
    this.connectToTelemetryStream();
  }

  ngOnDestroy() {
    this.isComponentDestroyed = true;
    this.clearReconnectTimer();
    this.telemetrySocket?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  get hasTelemetryData(): boolean {
    return this.telemetryHistory.length > 0;
  }

  get sampleWindowLabel(): string {
    return `Ultimi ${this.maxDataPoints} campioni`;
  }

  get maxSamplesCount(): number {
    return this.maxDataPoints;
  }

  get latestMetrics(): ITelemetryMetrics | null {
    return this.latestTelemetry?.metrics || null;
  }

  get connectionLabel(): string {
    switch (this.connectionState) {
      case 'connected':
        return 'Stream attivo';
      case 'connecting':
        return 'Connessione in corso';
      case 'error':
        return 'Errore di connessione';
      default:
        return 'Riconnessione in corso';
    }
  }

  get currentSamplesCount(): number {
    return this.telemetryHistory.length;
  }

  formatBytes(bytes?: number | null, decimals: number = 1): string {
    if (bytes === null || bytes === undefined || Number.isNaN(bytes)) {
      return '--';
    }

    if (bytes === 0) {
      return '0 B';
    }

    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    const unitIndex = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
    const value = bytes / Math.pow(1024, unitIndex);

    return `${value.toLocaleString('it-IT', {
      minimumFractionDigits: unitIndex === 0 ? 0 : decimals,
      maximumFractionDigits: unitIndex === 0 ? 0 : decimals
    })} ${units[unitIndex]}`;
  }

  formatDuration(totalSeconds?: number | null): string {
    if (totalSeconds === null || totalSeconds === undefined || Number.isNaN(totalSeconds)) {
      return '--';
    }

    const seconds = Math.max(0, Math.floor(totalSeconds));
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;

    if (days > 0) {
      return `${days}g ${hours}h ${minutes}m`;
    }

    return [hours, minutes, remainingSeconds]
      .map((value) => value.toString().padStart(2, '0'))
      .join(':');
  }

  formatDateTime(value?: string | null): string {
    if (!value) {
      return '--';
    }

    const parsedDate = new Date(value);

    if (Number.isNaN(parsedDate.getTime())) {
      return '--';
    }

    return parsedDate.toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  }

  private buildTelemetrySocketUrl(): string {
    if (!this.deviceLabel || !this.accessToken) {
      return '';
    }

    const encodedDeviceId = encodeURIComponent(this.deviceLabel);
    const encodedToken = encodeURIComponent(this.accessToken);

    return `${environment.wsUrl}iot-devices/telemetry/ws?deviceId=${encodedDeviceId}&token=${encodedToken}`;
  }

  private connectToTelemetryStream(): void {
    if (this.isComponentDestroyed || this.monitoringAccessLost) return;
    this.clearReconnectTimer();

    if (!this.telemetrySocketUrl) {
      this.connectionState = 'error';
      this.connectionMessage = !this.deviceLabel
        ? 'Device non trovato nella rotta corrente.'
        : 'Token di accesso non disponibile.';
      return;
    }

    this.connectionState = 'connecting';
    this.connectionMessage = `Apertura stream websocket per ${this.deviceLabel}...`;
    this.lastError = null;

    this.telemetrySocket = webSocket<ITelemetrySocketMessage | { type: 'pong'; timestamp: string }>({
      url: this.telemetrySocketUrl,
      openObserver: {
        next: () => {
          this.connectionState = 'connected';
          this.connectionMessage = 'Connessione attiva. In attesa dei campioni realtime.';
        }
      },
      closeObserver: {
        next: (event: CloseEvent) => {
          if (this.isComponentDestroyed) {
            return;
          }

          if (event.code === 4003 && ['MONITORING_SELECTION_CHANGED', 'MONITORING_UNIT_EXCLUDED'].includes(event.reason)) {
            this.monitoringAccessLost = true;
            this.clearReconnectTimer();
            this.latestTelemetry = null; this.telemetryHistory = [];
            this.refreshChartOptions();
            this.connectionState = 'disconnected';
            this.connectionMessage = 'Il piano non è più selezionato per il monitoraggio.';
            const buildingId = this.monitoringPreferences?.activeBuildingId;
            if (buildingId) this.monitoringPreferences?.changed({ buildingId });
            return;
          }

          if (this.connectionState !== 'error') {
            this.connectionState = 'disconnected';
            this.connectionMessage = 'Connessione chiusa. Riprovo automaticamente...';
          }

          this.scheduleReconnect();
        }
      }
    });

    this.telemetrySocket.pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (message) => this.handleSocketMessage(message as ITelemetrySocketMessage),
      error: (error) => {
        if (this.isComponentDestroyed) {
          return;
        }

        this.connectionState = 'error';
        this.connectionMessage = 'Errore websocket. Riprovo automaticamente...';
        this.lastError = this.resolveSocketError(error);
        this.scheduleReconnect();
      }
    });
  }

  private handleSocketMessage(message: ITelemetrySocketMessage): void {
    switch (message.type) {
      case 'ping':
        this.telemetrySocket?.next({
          type: 'pong',
          timestamp: new Date().toISOString()
        });
        return;

      case 'error':
        this.connectionState = 'error';
        this.connectionMessage = message.message || 'Il backend ha risposto con un errore.';
        this.lastError = message.message || 'Errore ricevuto dal websocket.';
        this.scheduleReconnect();
        return;

      case 'device_telemetry':
        if (!message.data || message.data.deviceId !== this.deviceLabel) {
          return;
        }

        this.latestTelemetry = message.data;
        this.connectionState = 'connected';
        this.connectionMessage = 'Stream realtime attivo.';
        this.telemetryHistory = this.trimTelemetryHistory([
          ...this.telemetryHistory,
          this.mapTelemetryPoint(message.data)
        ]);
        this.refreshChartOptions();
        return;

      default:
        return;
    }
  }

  private trimTelemetryHistory(history: ITelemetryPoint[]): ITelemetryPoint[] {
    return history.slice(-this.maxDataPoints);
  }

  private mapTelemetryPoint(payload: ITelemetryPayload): ITelemetryPoint {
    const parsedDate = new Date(payload.timestamp);
    const pointDate = Number.isNaN(parsedDate.getTime()) ? new Date() : parsedDate;

    return {
      label: pointDate.toLocaleTimeString('it-IT', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      }),
      cpuUsage: this.toNumberOrNull(payload.metrics.cpu_usage_pct),
      memoryUsage: this.toNumberOrNull(payload.metrics.memory_usage_pct),
      diskUsage: this.toNumberOrNull(payload.metrics.disk_usage_pct),
      temperature: this.toNumberOrNull(payload.metrics.temperature_celsius),
      wifiSignal: this.toNumberOrNull(payload.metrics.wifi?.signal_level_dbm),
      loadAverage: this.toNumberOrNull(payload.metrics.load_average_1m),
    };
  }

  private toNumberOrNull(value: number | null | undefined): number | null {
    return value === null || value === undefined || Number.isNaN(value) ? null : Number(value);
  }

  private scheduleReconnect(): void {
    if (this.isComponentDestroyed || this.monitoringAccessLost || this.reconnectTimer) {
      return;
    }

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = undefined;
      this.connectToTelemetryStream();
    }, this.reconnectDelayMs);
  }

  private clearReconnectTimer(): void {
    if (!this.reconnectTimer) {
      return;
    }

    clearTimeout(this.reconnectTimer);
    this.reconnectTimer = undefined;
  }

  private resolveSocketError(error: unknown): string {
    if (error instanceof Error && error.message) {
      return error.message;
    }

    if (typeof error === 'string' && error.trim()) {
      return error;
    }

    return 'Connessione non riuscita o terminata inaspettatamente.';
  }

  private refreshChartOptions(): void {
    const labels = this.telemetryHistory.map((point) => point.label);

    this.cpuChartOptions = this.buildLineChartOptions(labels, [
      {
        name: 'CPU',
        data: this.telemetryHistory.map((point) => point.cpuUsage),
        color: '#1976d2',
        areaColor: 'rgba(25, 118, 210, 0.18)',
        unit: '%',
        decimals: 1,
      }
    ], [
      { name: 'CPU', unit: '%', min: 0, max: 100 }
    ]);

    this.usageChartOptions = this.buildLineChartOptions(labels, [
      {
        name: 'RAM',
        data: this.telemetryHistory.map((point) => point.memoryUsage),
        color: '#26a69a',
        areaColor: 'rgba(38, 166, 154, 0.14)',
        unit: '%',
        decimals: 1,
      },
      {
        name: 'Disco',
        data: this.telemetryHistory.map((point) => point.diskUsage),
        color: '#42a5f5',
        areaColor: 'rgba(66, 165, 245, 0.12)',
        unit: '%',
        decimals: 1,
      }
    ], [
      { name: 'Utilizzo', unit: '%', min: 0, max: 100 }
    ], true);

    this.temperatureChartOptions = this.buildLineChartOptions(labels, [
      {
        name: 'Temperatura',
        data: this.telemetryHistory.map((point) => point.temperature),
        color: '#ef5350',
        areaColor: 'rgba(239, 83, 80, 0.16)',
        unit: 'deg C',
        decimals: 1,
      }
    ], [
      { name: 'Temp.', unit: 'deg C' }
    ]);

    this.connectivityChartOptions = this.buildLineChartOptions(labels, [
      {
        name: 'Wi-Fi',
        data: this.telemetryHistory.map((point) => point.wifiSignal),
        color: '#8e24aa',
        areaColor: 'rgba(142, 36, 170, 0.12)',
        unit: 'dBm',
        decimals: 0,
      },
      {
        name: 'Load 1m',
        data: this.telemetryHistory.map((point) => point.loadAverage),
        color: '#ffa726',
        areaColor: 'rgba(255, 167, 38, 0.10)',
        unit: '',
        decimals: 2,
        yAxisIndex: 1,
      }
    ], [
      { name: 'Segnale', unit: 'dBm', position: 'left' },
      { name: 'Load', unit: '', position: 'right', splitLine: false }
    ], true);
  }

  private buildLineChartOptions(
    labels: string[],
    series: IChartSeriesConfig[],
    axes: IChartAxisConfig[],
    showLegend: boolean = false
  ): EChartsOption {
    return {
      animation: true,
      animationDuration: 250,
      animationEasing: 'cubicOut',
      tooltip: {
        trigger: 'axis',
        backgroundColor: 'rgba(35, 40, 55, 0.94)',
        borderWidth: 0,
        textStyle: {
          color: '#ffffff',
          fontSize: 12
        },
        axisPointer: {
          type: 'line',
          lineStyle: {
            color: '#90a4ae',
            type: 'dashed'
          }
        },
        formatter: (params: any) => {
          const items = Array.isArray(params) ? params : [params];
          const lines = items.map((item: any) => {
            const currentSeries = series[item.seriesIndex];

            if (!currentSeries || item.data === null || item.data === undefined) {
              return '';
            }

            return `${item.marker} ${currentSeries.name}: <strong>${this.formatSeriesValue(item.data, currentSeries.unit, currentSeries.decimals)}</strong>`;
          }).filter(Boolean);

          return [items[0]?.axisValueLabel || '', ...lines].join('<br/>');
        }
      },
      legend: showLegend ? {
        top: 0,
        left: 0,
        icon: 'roundRect',
        itemWidth: 12,
        itemHeight: 8,
        textStyle: {
          color: '#546e7a',
          fontSize: 12,
          fontWeight: 600
        }
      } : undefined,
      grid: {
        left: '4%',
        right: '4%',
        top: showLegend ? 48 : 18,
        bottom: '10%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: labels,
        axisTick: { show: false },
        axisLine: {
          lineStyle: {
            color: '#d9e0e6'
          }
        },
        axisLabel: {
          color: '#78909c',
          fontSize: 11
        }
      },
      yAxis: axes.map((axis) => ({
        type: 'value',
        name: axis.name,
        min: axis.min,
        max: axis.max,
        position: axis.position || 'left',
        nameTextStyle: {
          color: '#78909c',
          fontSize: 11,
          padding: [0, 0, 6, 0]
        },
        axisLabel: {
          color: '#78909c',
          fontSize: 11,
          formatter: (value: number) => axis.unit ? `${value} ${axis.unit}` : `${value}`
        },
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: {
          show: axis.splitLine !== false,
          lineStyle: {
            color: 'rgba(0, 0, 0, 0.08)',
            type: 'dashed'
          }
        }
      })),
      series: series.map((entry) => ({
        name: entry.name,
        type: 'line',
        smooth: true,
        symbol: 'circle',
        symbolSize: 7,
        showSymbol: true,
        connectNulls: false,
        yAxisIndex: entry.yAxisIndex || 0,
        data: entry.data,
        lineStyle: {
          width: 3,
          color: entry.color
        },
        itemStyle: {
          color: entry.color
        },
        areaStyle: {
          color: entry.areaColor
        },
        emphasis: {
          focus: 'series'
        }
      }))
    };
  }

  private formatSeriesValue(value: number, unit?: string, decimals: number = 1): string {
    const formattedValue = Number(value).toLocaleString('it-IT', {
      minimumFractionDigits: decimals === 0 ? 0 : 1,
      maximumFractionDigits: decimals
    });

    return unit ? `${formattedValue} ${unit}` : formattedValue;
  }
}
